/**
 * Sidebar da home da plataforma: marca, lista de cursos e ações de conta.
 *
 * Não confundir com components/Sidebar.tsx, que é o menu de DENTRO de um curso
 * (Dashboard, Videoaulas, ...). Esta lista cursos, aquela lista seções.
 *
 * Abaixo de `nav` (900px) vira drawer, controlado pelo PlatformHome. Antes ela
 * simplesmente sumia no mobile, e com isso não havia como trocar de curso pelo
 * celular a não ser rolando até o carrossel.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import MedverseLogo from './MedverseLogo';
import type { ResolvedCourse } from './mergeCourses';
import { GearIcon, LockIcon, PlaySquareIcon, UsersIcon } from './icons';

interface PlatformSidebarProps {
  courses: ResolvedCourse[];
  onSelect: (course: ResolvedCourse) => void;
  /** Estado do drawer. Acima de `nav` é ignorado: a coluna é sempre visível. */
  isOpen?: boolean;
  onClose?: () => void;
  /** Só renderiza o item de Administração quando definido (admin com curso ativo). */
  onAdmin?: () => void;
  /** Só renderiza Configurações quando há sessão (a tela vive dentro do curso). */
  onSettings?: () => void;
  /** Marca cursos sem matrícula. Só com sessão — ver CourseCard. */
  showAccessState?: boolean;
}

const itemBase =
  'relative flex w-full items-center gap-3 rounded-[10px] px-3 py-[11px] text-left text-sm transition-colors duration-200 ease-mv';

const PlatformSidebar: React.FC<PlatformSidebarProps> = ({
  courses,
  onSelect,
  isOpen = false,
  onClose,
  onAdmin,
  onSettings,
  showAccessState = false,
}) => {
  const { t } = useTranslation();

  return (
    <aside
      id="platform-nav"
      className={`fixed inset-y-0 left-0 z-40 flex w-[244px] flex-col border-r border-white/10 bg-mv-bg pt-[26px] pb-[18px]
        transition-transform duration-300 ease-mv
        nav:static nav:visible nav:translate-x-0
        ${isOpen ? 'translate-x-0' : 'invisible -translate-x-full'}`}
    >
      <div className="px-[22px] pb-[26px]">
        <MedverseLogo />
      </div>

      <p className="px-6 pb-3 text-[10.5px] font-bold uppercase tracking-[0.18em] text-mv-muted">
        {t('platform.coursesLabel')}
      </p>

      <nav className="no-scrollbar flex-1 space-y-0.5 overflow-y-auto px-3 pb-4">
        {courses.map((course) => {
          const isSoon = course.status === 'soon';
          const isLocked = showAccessState && !isSoon && !course.enrolled && !course.externalUrl;

          return (
            <button
              key={course.slug}
              type="button"
              onClick={() => onSelect(course)}
              aria-current={course.isCurrent ? 'true' : undefined}
              className={`${itemBase} ${
                course.isCurrent
                  ? // A barra magenta de 3px na borda esquerda é a assinatura do
                    // item ativo no mockup.
                    'mv-nav-active font-semibold text-mv-text before:absolute before:inset-y-2 before:left-0 before:w-[3px] before:rounded-r-[3px] before:bg-mv-accent before:content-[""]'
                  : isSoon
                    ? 'cursor-default font-medium text-mv-muted/60'
                    : 'font-medium text-mv-muted hover:bg-mv-surface hover:text-mv-text'
              }`}
            >
              <PlaySquareIcon
                className={`h-[17px] w-[17px] ${course.isCurrent ? 'text-mv-accent-hi' : 'opacity-85'}`}
              />
              <span className="flex-1 truncate">{course.title}</span>
              {isLocked && <LockIcon className="h-3.5 w-3.5 opacity-60" />}
            </button>
          );
        })}
      </nav>

      {(onAdmin || onSettings) && (
        <div className="mt-3 space-y-0.5 border-t border-white/10 px-3 pt-3.5">
          {onAdmin && (
            <button
              type="button"
              onClick={() => {
                onClose?.();
                onAdmin();
              }}
              className={`${itemBase} font-semibold text-mv-muted hover:bg-mv-surface hover:text-mv-text`}
            >
              <UsersIcon className="h-[17px] w-[17px]" />
              <span>{t('sidebar.admin')}</span>
            </button>
          )}
          {onSettings && (
            <button
              type="button"
              onClick={() => {
                onClose?.();
                onSettings();
              }}
              data-testid="platform-settings-button"
              className={`${itemBase} font-semibold text-mv-muted hover:bg-mv-surface hover:text-mv-text`}
            >
              <GearIcon className="h-[17px] w-[17px]" />
              <span>{t('sidebar.settings')}</span>
            </button>
          )}
        </div>
      )}
    </aside>
  );
};

export default PlatformSidebar;
